import React, { useState } from "react"

import * as Sentry from "@sentry/react"
import { format } from "date-fns"
import { AlertCircle, Ban, Clock, Home, Mail, XCircle } from "lucide-react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Link } from "react-router-dom"

import AppLayout from "@/components/layout/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { SUPPORT_EMAIL } from "@/constants/contact"
import { useToast } from "@/hooks/use-toast"
import { loginWithMagicLink } from "@/services/authService"
import { ApplicationStatus, toApplicationStatus } from "@/utils/stageRedirectUtils"

interface ApplicationErrorProps {
  status?: string
  applicationDate?: string
  message?: string
}

const ApplicationError: React.FC<ApplicationErrorProps> = ({
  status,
  applicationDate,
  message,
}) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isSending, setIsSending] = useState(false)
  const { toast } = useToast()

  // Get error details from URL params if not provided as props
  const errorStatus = status || searchParams.get("status")
  const errorDate = applicationDate || searchParams.get("applicationDate") || ""
  const errorMessage =
    message || searchParams.get("message") || "You already have a recent application for this job."
  const normalizedStatus = toApplicationStatus(errorStatus)

  // Extract application ID from URL if available
  const applicationId = searchParams.get("applicationId") || ""
  // Extract email from URL if available
  const email = searchParams.get("email") || ""

  // Format the application date for display
  const formattedDate = errorDate ? format(new Date(errorDate), "MMMM d, yyyy") : ""

  // Determine message based on status
  const getStatusMessage = () => {
    switch (normalizedStatus) {
      case ApplicationStatus.InProgress:
        return "Your application is currently in progress."
      case ApplicationStatus.Hold:
        return "Your application is currently on hold."
      case ApplicationStatus.Rejected:
        return "Your application was recently rejected."
      case ApplicationStatus.Pending:
        return "You are already active with another employer. Our staff needs to review your applications before you can continue. You should expect to be contacted by staff who will determine whether you can continue with your application."
      case ApplicationStatus.Onboarded:
        return "You were onboarded for this position."
      case ApplicationStatus.Banned:
        return "Your account has been banned. If you believe this is an error, please contact support."
      default:
        return "Your application is being processed."
    }
  }

  // Handle continue application - send magic link to continue existing application
  const handleContinueApplication = async () => {
    setIsSending(true)

    try {
      // Get the application ID from URL params or use the existing one from the application that caused the error
      const appId = applicationId || searchParams.get("applicationId")
      // Get the email from URL params
      const userEmail = email || searchParams.get("email")

      if (!appId || !userEmail) {
        toast({
          title: "Error",
          description: "Missing application information. Please try again.",
          variant: "destructive",
        })
        setIsSending(false)
        return
      }

      // Get the current origin for redirect URL - use auth-callback
      const redirectUrl = `${window.location.origin}/auth-callback`

      // Send magic link to continue the application
      const result = await loginWithMagicLink(userEmail, appId, redirectUrl)

      if (result.success) {
        toast({
          title: "Email Sent",
          description:
            "We've sent a magic link to your email address to continue your application.",
          variant: "default",
        })
      } else {
        throw new Error(result.message || "Failed to send email")
      }
    } catch (error) {
      Sentry.captureException(error)
      toast({
        title: "Error",
        description: "Failed to send magic link. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSending(false)
    }
  }

  // If status is Rejected, show rejected-specific UI (similar to ApplicationRejectedPage)
  if (normalizedStatus === ApplicationStatus.Rejected) {
    return (
      <AppLayout>
        <div className="container mx-auto max-w-lg px-4 py-6">
          <Card>
            <CardHeader className="text-center">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-red-100 p-3">
                  <XCircle className="h-8 w-8 text-red-600" />
                </div>
              </div>
              <CardTitle className="text-xl font-bold text-jungle">Application Rejected</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="mb-2 font-medium text-red-800">Your application was not successful</p>
                <p className="text-sm text-red-700">
                  {errorMessage ||
                    "Unfortunately, we are unable to proceed with your application at this time. Thank you for your interest."}
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">What happens next?</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="mr-3 mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-red-500"></span>
                    You can reapply in 1 month
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-red-500"></span>
                    Review our requirements for future applications
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-red-500"></span>
                    Consider gaining additional experience
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-600">
                  If you have questions about your application, please contact us at{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-honeycomb hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                </p>
              </div>
            </CardContent>

            <div className="space-y-3 p-6 pt-0">
              <Link to="/" className="block w-full">
                <Button className="w-full bg-honeycomb hover:bg-amber-500">
                  <Home className="mr-2 h-4 w-4" /> Return to Home
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </AppLayout>
    )
  }

  // If status is Hold, show hold-specific UI (similar to HoldPage)
  if (normalizedStatus === ApplicationStatus.Hold) {
    return (
      <AppLayout>
        <div className="container mx-auto max-w-lg px-4 py-6">
          <Card>
            <CardHeader className="text-center">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-amber-100 p-3">
                  <Clock className="h-8 w-8 text-amber-600" />
                </div>
              </div>
              <CardTitle className="text-xl font-bold text-jungle">Application On Hold</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="mb-2 font-medium text-amber-800">
                  Your application is currently on hold
                </p>
                <p className="text-sm text-amber-700">
                  {errorMessage ||
                    "We're reviewing your application and may need additional information. Our team will contact you soon with next steps."}
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">What happens next?</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="mr-3 mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-amber-500"></span>
                    Our team will review your application
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-amber-500"></span>
                    You'll receive an email update within 2-3 business days
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-amber-500"></span>
                    Check your application status anytime using this portal
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-600">
                  If you have questions about your application status, please contact us at{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-honeycomb hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                </p>
              </div>
            </CardContent>

            <div className="space-y-3 p-6 pt-0">
              <Link to="/application-status" className="block w-full">
                <Button variant="outline" className="w-full">
                  Check Application Status
                </Button>
              </Link>
              <Link to="/" className="block w-full">
                <Button className="w-full bg-honeycomb hover:bg-amber-500">
                  <Home className="mr-2 h-4 w-4" /> Return to Home
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </AppLayout>
    )
  }

  // If status is Pending, show pending-specific UI (similar to PendingPage)
  if (normalizedStatus === ApplicationStatus.Pending) {
    return (
      <AppLayout>
        <div className="container mx-auto max-w-lg px-4 py-6">
          <Card>
            <CardHeader className="text-center">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-amber-100 p-3">
                  <Clock className="h-8 w-8 text-amber-600" />
                </div>
              </div>
              <CardTitle className="text-xl font-bold text-jungle">
                Application Pending Review
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="mb-2 font-medium text-amber-800">
                  Your application is pending review
                </p>
                <p className="text-sm text-amber-700">{getStatusMessage()}</p>
              </div>

              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">What happens next?</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="mr-3 mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-amber-500"></span>
                    Our staff will review your application status
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-amber-500"></span>
                    You'll be contacted by our team regarding your application
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-amber-500"></span>
                    Staff will determine if you can continue with your application
                  </li>
                </ul>
              </div>

              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-600">
                  If you have questions about your application status, please contact us at{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-honeycomb hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                </p>
              </div>
            </CardContent>

            <div className="space-y-3 p-6 pt-0">
              <Link to="/application-status" className="block w-full">
                <Button variant="outline" className="w-full">
                  Check Application Status
                </Button>
              </Link>
              <Link to="/" className="block w-full">
                <Button className="w-full bg-honeycomb hover:bg-amber-500">
                  <Home className="mr-2 h-4 w-4" /> Return to Home
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-6">
        <div className="mx-auto max-w-lg rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-amber-100 p-4">
              <Ban className="h-16 w-16 text-amber-500" />
            </div>
          </div>

          <h1 className="mb-4 text-2xl font-bold text-jungle">New Application Not Allowed</h1>

          <p className="mb-6 text-gray-600">{errorMessage}</p>

          <div className="mb-6 rounded-md border border-amber-100 bg-amber-50 p-4">
            <h2 className="mb-2 text-lg font-semibold">Application Status</h2>
            <p className="mb-1">
              <span className="font-medium">Status:</span> {errorStatus}
            </p>
            {formattedDate && (
              <p>
                <span className="font-medium">Date:</span> {formattedDate}
              </p>
            )}
            <p className="mt-3 text-sm text-gray-700">{getStatusMessage()}</p>
          </div>

          {errorStatus.toLowerCase() !== "banned" && (
            <div className="mb-6 flex items-start rounded-md border border-blue-100 bg-blue-50 p-4 text-left">
              <AlertCircle className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
              <div>
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Please note:</span> You can apply again for this
                  position after 30 days from your last application.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {normalizedStatus === ApplicationStatus.InProgress && (
              <Button
                onClick={handleContinueApplication}
                className="w-full bg-honeycomb text-white hover:bg-amber-500"
                disabled={isSending}
              >
                {isSending ? (
                  <>
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Continue Application
                  </>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate("/")}
              disabled={isSending}
            >
              Back to Jobs
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

export default ApplicationError
